import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';
import SimpleBarReact from 'simplebar-react';

import { FreeHotel } from 'components/FreeHotel/FreeHotel';
import PremiumHotel from 'components/PremiumHotel';

import type { DirectoryCardFields } from 'hooks/useDirectoryConfig/types';
import type { FreeHotelItem, PremiumHotelItem } from 'hooks/useDirectoryData/types';
import { useSettings } from 'hooks/useSettings';

const buildImageUrl = (appId: string | null, value: string | null | undefined): string | null => {
  return value ? `https://front.travpromobile.com/${appId}/${value}` : null;
};

interface HotelsListProps {
  premiumHotelsList: PremiumHotelItem[];
  freeHotelsList: FreeHotelItem[];
  loading: boolean;
  filterOpen: boolean;
  zoom: number;
  cardFields: DirectoryCardFields;
  onFlyToMarker: (lat: number | string, lon: number | string) => void;
  onPremiumInfo: (userInfoID: string, name: string) => void;
  onFreeInfo: (listingId: string, name: string) => void;
  onResetMap: () => void;
}

function HotelsList({
  premiumHotelsList,
  freeHotelsList,
  loading,
  filterOpen,
  zoom,
  cardFields,
  onFlyToMarker,
  onPremiumInfo,
  onFreeInfo,
  onResetMap,
}: HotelsListProps) {
  const { appId } = useSettings();
  const hasPremiumContent = loading || premiumHotelsList.length > 0;
  const hasBothLists = hasPremiumContent && freeHotelsList.length > 0;

  return (
    <SimpleBarReact className={`listContainer ${filterOpen ? 'filterOpen' : ''}`}>
      {hasPremiumContent && (
        <div className="premiumList">
          {loading && (
            <span className="loader">
              <FontAwesomeIcon icon={['fas', 'circle-notch']} spin />
            </span>
          )}
          {premiumHotelsList.map((hotel) => {
            return (
              <PremiumHotel
                key={hotel.id}
                id={hotel.id}
                bgImage={hotel.headerImages[0]}
                propertyName={hotel.propertyName}
                subCatname={hotel.sub_catname}
                flyToMarker={() => {
                  onFlyToMarker(hotel.coords.latitude, hotel.coords.longitude);
                }}
                hotelInfo={() => {
                  onPremiumInfo(hotel.userInfoID, hotel.propertyName);
                }}
              />
            );
          })}
        </div>
      )}
      <div className={clsx('freeList', { hasBothLists })}>
        {freeHotelsList.length === 0 && premiumHotelsList.length === 0 && !loading && (
          <div className="noResults">{i18n.translate('directoryView.noResults')}</div>
        )}
        {freeHotelsList.map((hotel, i) => {
          return (
            <React.Fragment key={hotel.company_listing_id}>
              {/* Deduplicate by company name — API returns one entry per category */}
              {(i === 0 || freeHotelsList[i - 1]?.company !== hotel.company) && (
                <FreeHotel
                  id={hotel.company_listing_id.toString()}
                  flyToMarker={() => {
                    onFlyToMarker(hotel.latitude, hotel.longitude);
                  }}
                  hotelInfo={() => {
                    onFreeInfo(hotel.listing_id.toString(), hotel.company);
                  }}
                  hotelName={hotel.company}
                  hotel={hotel}
                  cardFields={cardFields}
                  brandImageUrl={buildImageUrl(appId, hotel.brand)}
                  segmentImageUrl={buildImageUrl(appId, hotel.segment)}
                />
              )}
            </React.Fragment>
          );
        })}
        {zoom > 8 && (
          <div className="resetMap" onClick={onResetMap}>
            {i18n.translate('directoryView.resetMap')}
          </div>
        )}
      </div>
    </SimpleBarReact>
  );
}

export default HotelsList;
